(() => {
  'use strict';

  const MOBILE_BREAKPOINT = 749;
  const MOBILE_PAGE_SIZE = 3;

  const sections = new WeakMap();


  /* =========================================================
     UTILITIES
     ========================================================= */

  const isMobile = () =>
    window.innerWidth <= MOBILE_BREAKPOINT;


  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase();


  const getCategories = (card) =>
    String(card.dataset.categories || '')
      .split(',')
      .map(normalize)
      .filter(Boolean);


  /* =========================================================
     CATEGORY MATCHING
     ========================================================= */

  const cardMatchesCategory = (
    card,
    category
  ) => {
    const selected = normalize(category);

    if (!selected) {
      return true;
    }

    return getCategories(card).includes(selected);
  };


  /* =========================================================
     ACTIVE CATEGORY
     ========================================================= */

  const updateActiveCategories = (
    section,
    category
  ) => {
    section.categoryButtons.forEach(
      (button) => {

        const buttonCategory =
          normalize(
            button.dataset.category
          );

        button.classList.toggle(
          'is-active',
          buttonCategory === normalize(category)
        );

      }
    );
  };


  /* =========================================================
     FILTER DESKTOP
     ========================================================= */

  const renderDesktopCards = (
    section
  ) => {

    section.desktopCards.forEach(
      (card) => {

        const visible =
          cardMatchesCategory(
            card,
            section.selectedCategory
          );

        card.hidden = !visible;

      }
    );

  };


  /* =========================================================
     FILTER MOBILE
     ========================================================= */

  const getMobileMatchingCards = (
    section
  ) => {

    return section.mobileCards.filter(
      (card) =>
        cardMatchesCategory(
          card,
          section.selectedCategory
        )
    );

  };


  const renderMobileCards = (
    section
  ) => {

    const matchingCards =
      getMobileMatchingCards(section);


    const totalPages = Math.max(
      1,
      Math.ceil(
        matchingCards.length /
        MOBILE_PAGE_SIZE
      )
    );


    section.mobilePage = Math.min(
      section.mobilePage,
      totalPages
    );


    const start =
      (section.mobilePage - 1) *
      MOBILE_PAGE_SIZE;

    const end =
      start +
      MOBILE_PAGE_SIZE;


    section.mobileCards.forEach(
      (card) => {

        const index =
          matchingCards.indexOf(card);

        const visible =
          index >= start &&
          index < end;


        card.hidden = !visible;

      }
    );


    /*
     * Restart entrance animation for
     * cards appearing on the new page.
     */
    matchingCards
      .slice(start, end)
      .forEach(
        (card) => {

          card.classList.remove(
            'is-visible'
          );


          requestAnimationFrame(
            () => {

              requestAnimationFrame(
                () => {
                  card.classList.add(
                    'is-visible'
                  );
                }
              );

            }
          );

        }
      );


    return totalPages;
  };


  /* =========================================================
     MOBILE PAGINATION
     ========================================================= */

  const renderMobilePagination = (
    section
  ) => {

    if (!section.mobilePagination) {
      return;
    }


    const pagesContainer =
      section.mobilePagination.querySelector(
        '[data-mobile-pages]'
      );

    const previous =
      section.mobilePagination.querySelector(
        '[data-mobile-prev]'
      );

    const next =
      section.mobilePagination.querySelector(
        '[data-mobile-next]'
      );


    if (
      !pagesContainer ||
      !previous ||
      !next
    ) {
      return;
    }


    const matchingCards =
      getMobileMatchingCards(section);


    const totalPages = Math.max(
      1,
      Math.ceil(
        matchingCards.length /
        MOBILE_PAGE_SIZE
      )
    );


    section.mobilePage = Math.min(
      section.mobilePage,
      totalPages
    );


    pagesContainer.innerHTML = '';


    for (
      let page = 1;
      page <= totalPages;
      page++
    ) {

      const button =
        document.createElement('button');


      button.type = 'button';

      button.className =
        'kyudo-blog-page__mobile-number';

      button.textContent =
        String(page);


      if (
        page === section.mobilePage
      ) {

        button.classList.add(
          'is-current'
        );

        button.setAttribute(
          'aria-current',
          'page'
        );

      }


      button.addEventListener(
        'click',
        () => {

          section.mobilePage =
            page;


          renderMobileCards(
            section
          );

          renderMobilePagination(
            section
          );

          scrollToMobileBlogs(
            section
          );

        }
      );


      pagesContainer.appendChild(
        button
      );

    }


    previous.disabled =
      section.mobilePage === 1;


    next.disabled =
      section.mobilePage === totalPages;

  };


  /* =========================================================
     MOBILE PREV / NEXT
     ========================================================= */

  const setupMobilePagination = (
    section
  ) => {

    if (!section.mobilePagination) {
      return;
    }


    const previous =
      section.mobilePagination.querySelector(
        '[data-mobile-prev]'
      );

    const next =
      section.mobilePagination.querySelector(
        '[data-mobile-next]'
      );


    if (!previous || !next) {
      return;
    }


    previous.addEventListener(
      'click',
      () => {

        if (
          section.mobilePage <= 1
        ) {
          return;
        }


        section.mobilePage--;


        renderMobileCards(
          section
        );

        renderMobilePagination(
          section
        );

        scrollToMobileBlogs(
          section
        );

      }
    );


    next.addEventListener(
      'click',
      () => {

        const matchingCards =
          getMobileMatchingCards(
            section
          );


        const totalPages =
          Math.max(
            1,
            Math.ceil(
              matchingCards.length /
              MOBILE_PAGE_SIZE
            )
          );


        if (
          section.mobilePage >=
          totalPages
        ) {
          return;
        }


        section.mobilePage++;


        renderMobileCards(
          section
        );

        renderMobilePagination(
          section
        );

        scrollToMobileBlogs(
          section
        );

      }
    );

  };


  /* =========================================================
     MOBILE CATEGORY COLLAPSER
     ========================================================= */

  const setupMobileCategoryToggle = (
    section
  ) => {

    const toggle =
      section.mobileCategoryToggle;

    const panel =
      section.mobileCategoryPanel;


    if (!toggle || !panel) {
      return;
    }


    toggle.addEventListener(
      'click',
      () => {

        const expanded =
          toggle.getAttribute(
            'aria-expanded'
          ) === 'true';


        toggle.setAttribute(
          'aria-expanded',
          String(!expanded)
        );

      }
    );

  };


  /* =========================================================
     SCROLL MOBILE BLOGS
     ========================================================= */

  const scrollToMobileBlogs = (
    section
  ) => {

    if (!isMobile()) {
      return;
    }


    const main =
      section.mobileMain;


    if (!main) {
      return;
    }


    const top =
      main.getBoundingClientRect().top +
      window.scrollY -
      20;


    window.scrollTo({
      top,
      behavior: 'smooth'
    });

  };


  /* =========================================================
     CATEGORY CLICK
     ========================================================= */

  const setupCategoryEvents = (
    section
  ) => {

    section.categoryButtons.forEach(
      (button) => {

        button.addEventListener(
          'click',
          (event) => {

            event.preventDefault();


            section.selectedCategory =
              button.dataset.category || '';


            section.mobilePage = 1;


            /*
             * Update active category in BOTH
             * desktop and mobile versions.
             */
            updateActiveCategories(
              section,
              section.selectedCategory
            );


            /*
             * Render both versions independently.
             */
            renderDesktopCards(
              section
            );


            renderMobileCards(
              section
            );


            renderMobilePagination(
              section
            );


            /*
             * Close mobile category menu
             * after selecting a category.
             */
            if (isMobile()) {

              const toggle =
                section.mobileCategoryToggle;


              if (toggle) {

                toggle.setAttribute(
                  'aria-expanded',
                  'false'
                );

              }


              scrollToMobileBlogs(
                section
              );

            }

          }
        );

      }
    );

  };


  /* =========================================================
     INITIAL MOBILE CARD STATE
     ========================================================= */

  const initializeMobileCards = (
    section
  ) => {

    if (!section.mobileCards.length) {
      return;
    }


    renderMobileCards(
      section
    );

    renderMobilePagination(
      section
    );

  };


  /* =========================================================
     INITIALIZE SECTION
     ========================================================= */

  const initializeSection = (
    root
  ) => {

    if (
      !root ||
      sections.has(root)
    ) {
      return;
    }


    const desktop =
      root.querySelector(
        '.kyudo-blog-page__desktop'
      );

    const mobile =
      root.querySelector(
        '.kyudo-blog-page__mobile'
      );


    if (!desktop || !mobile) {
      return;
    }


    const desktopCards =
      Array.from(
        desktop.querySelectorAll(
          '.kyudo-blog-card'
        )
      );


    const mobileCards =
      Array.from(
        mobile.querySelectorAll(
          '.kyudo-blog-card'
        )
      );


    const categoryButtons =
      Array.from(
        root.querySelectorAll(
          '.kyudo-blog-page__category'
        )
      );


    if (
      !desktopCards.length &&
      !mobileCards.length
    ) {
      return;
    }


    const initialButton =
      root.querySelector(
        '.kyudo-blog-page__category.is-active'
      );


    const state = {

      root,

      desktop,

      mobile,

      desktopCards,

      mobileCards,

      categoryButtons,

      selectedCategory:
        initialButton?.dataset.category ||
        '',

      mobilePage: 1,

      mobilePagination:
        mobile.querySelector(
          '.kyudo-blog-page__mobile-pagination'
        ),

      mobileMain:
        mobile.querySelector(
          '.kyudo-blog-page__mobile-main'
        ),

      mobileCategoryToggle:
        mobile.querySelector(
          '.kyudo-blog-page__mobile-category-toggle'
        ),

      mobileCategoryPanel:
        mobile.querySelector(
          '.kyudo-blog-page__mobile-category-panel'
        )

    };


    sections.set(
      root,
      state
    );


    /*
     * Synchronize initial active state.
     */
    updateActiveCategories(
      state,
      state.selectedCategory
    );


    setupCategoryEvents(
      state
    );


    setupMobileCategoryToggle(
      state
    );


    setupMobilePagination(
      state
    );


    renderDesktopCards(
      state
    );


    initializeMobileCards(
      state
    );

  };


  /* =========================================================
     RESPONSIVE UPDATE
     ========================================================= */

  const updateResponsiveSections = () => {

    document
      .querySelectorAll(
        '.kyudo-blog-page'
      )
      .forEach(
        (root) => {

          const state =
            sections.get(root);


          if (!state) {
            return;
          }


          if (isMobile()) {

            renderMobileCards(
              state
            );

            renderMobilePagination(
              state
            );

          } else {

            renderDesktopCards(
              state
            );

            /*
             * Keep mobile DOM prepared but hidden
             * by CSS.
             */
            state.mobileCards.forEach(
              (card) => {
                card.hidden = false;
              }
            );

          }

        }
      );

  };


  /* =========================================================
     INITIAL LOAD
     ========================================================= */

  const initializeAll = () => {

    document
      .querySelectorAll(
        '.kyudo-blog-page'
      )
      .forEach(
        initializeSection
      );

  };


  /* =========================================================
     RESIZE
     ========================================================= */

  let resizeTimer = null;


  window.addEventListener(
    'resize',
    () => {

      clearTimeout(
        resizeTimer
      );


      resizeTimer = setTimeout(
        updateResponsiveSections,
        120
      );

    },
    { passive: true }
  );


  /* =========================================================
     DOM READY
     ========================================================= */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initializeAll
    );

  } else {

    initializeAll();

  }


  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  if (
    window.Shopify?.designMode
  ) {

    document.addEventListener(
      'shopify:section:load',
      (event) => {

        const root =
          event.target?.querySelector(
            '.kyudo-blog-page'
          );


        if (root) {
          initializeSection(root);
        }

      }
    );

  }

})();